'use client'
import ArrowLeftIcon from '@precis-digital/kurama/icons/ArrowLeftIcon'
import Button from '@precis-digital/kurama/Button'
import Divider from '@precis-digital/kurama/Divider'
import Grid from '@precis-digital/kurama/Grid'
import ShopIcon from '@precis-digital/kurama/icons/ShopIcon'
import Typography from '@precis-digital/kurama/Typography'
import { styled } from '@precis-digital/kurama/utils'
import alienWithFlag from 'public/assets/images/alien_w_flag.png'
import alvieLogoFull from 'public/assets/images/alvie_logo_full.svg'
import { type ReactElement } from 'react'

import Head from 'next/head'
import ThemedImage from 'shared/components/Themed/Image'
import { APP_ROUTES } from 'shared/routes'
import { formatPageMetaTitle } from '../../utils/stringFormatter'
import { useChangePath, useGoBack } from '../Router'
import {
  StyledGridErrorCodeMessageContainer,
  StyledGridPageContainer,
  StyledGridRightSideContainer,
  StyledGridTextContainer,
  StyledGridTopSectionContainer
} from './styles'

const NotFoundPage = ({
  returnPath,
  hasDashboardFrame = false
}: {
  returnPath?: string
  hasDashboardFrame?: boolean
}): ReactElement => {
  const { changePath } = useChangePath()
  const goBack = useGoBack()
  const handleTakeMeBackClick = (): void => {
    if (returnPath != null) {
      void changePath(returnPath)
    } else {
      goBack()
    }
  }

  const handleTakeMeHomeClick = (): void => {
    void changePath(APP_ROUTES.home.basePage)
  }

  return (
    <>
      <Head>
        <title>
          {formatPageMetaTitle({
            pageTitle: 'Page Not Found'
          })}
        </title>
      </Head>
      <StyledGridPageContainer hasDashboardFrame={hasDashboardFrame}>
        <StyledGridTopSectionContainer hasDashboardFrame={hasDashboardFrame}>
          <StyledGridImageContainer>
            <ThemedImage
              src={alienWithFlag}
              alt="Alien with Flag"
              style={{
                maxWidth: '100%',
                height: 'auto'
              }}
            />
          </StyledGridImageContainer>
          {!hasDashboardFrame && (
            <StyledGridLogoContainer onClick={handleTakeMeHomeClick}>
              <ThemedImage
                src={alvieLogoFull}
                alt="Alvie Logo Icon Blue"
                width={128}
                height={64}
                style={{
                  maxWidth: '100%',
                  height: 'auto'
                }}
              />{' '}
            </StyledGridLogoContainer>
          )}
          <StyledGridRightSideContainer>
            <StyledGridTextContainer>
              <Typography variant="h2">
                Oh, man. This guy is totally lost...
                <br />
                And so are you.
              </Typography>
              <Typography variant="body2">
                Either something went wrong or the page doesn't exist anymore. We're as shocked as you are - but don't
                worry, there's an easy fix:
              </Typography>
            </StyledGridTextContainer>
            <Grid alignSelf="center" gap="8px" display="flex">
              <Button leftIcon={<ArrowLeftIcon />} variant="filled" onClick={handleTakeMeBackClick}>
                Take me back
              </Button>
              <Button leftIcon={<ShopIcon />} variant="tonal" onClick={handleTakeMeHomeClick}>
                Take me home
              </Button>
            </Grid>
          </StyledGridRightSideContainer>
        </StyledGridTopSectionContainer>
        <Divider />
        <StyledGridErrorCodeMessageContainer>
          <Typography variant="h4">404</Typography>
          <Typography variant="body2">
            - That's just an error which means that what you were looking for does not exist.
          </Typography>
        </StyledGridErrorCodeMessageContainer>
      </StyledGridPageContainer>
    </>
  )
}

const StyledGridLogoContainer = styled(Grid)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  display: 'flex',
  gap: '26px',
  padding: '14px',
  ':hover': {
    cursor: 'pointer',
    backgroundColor: theme.tokens.background.main.pressed
  },
  borderRadius: '16px'
}))

const StyledGridImageContainer = styled(Grid)(() => ({
  width: '40%'
}))

export default NotFoundPage
